// A guided conversation (28 Oct 2026; design/2026-10-28-guided-conversations.md):
// /guided/:customer/:responseId, the page a person's invitation opens.
//
// Before they start: the guide's intro and the whole agenda (GuideIntro), so
// the structure is set when they arrive. Once started: the ordinary agent
// chat, made by the server with the `guided` profile, hosted the way the
// page helpers host theirs — the one AgentChat and its one composer, with the
// page's own section in the chat's right-hand panel (GuidePanel: where they
// are in the agenda, the notes, Finish now). Text only: dictation and file
// upload are the composer's own; there is no Talk.
//
// The response is the server's (useGuidedResponse): the agent ticks points
// and writes notes through its guide tools, and the page refetches when one
// of those calls ends and when a turn ends. A Manager may open the page and
// read; only the person writes.

import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, CircularProgress } from '@mui/material'
import axios from 'axios'
import { AgentChatProvider, AgentChat, useAgentChat, type AgentChatConfig, type AgentComposerHandle } from '../agent'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useWhoToAsk from '../hooks/useWhoToAsk'
import useDictationAccess from '../hooks/useDictationAccess'
import useGuidedResponse from '../hooks/useGuidedResponse'
import { API_BASE_URL } from '../constants/system'
import { AGENT_PLUGINS, getAuthToken, platinumV2Endpoints } from '../utils/agentTransport'
import { ptToolFormatter } from '../plugins/ptToolFormatter'
import { AGENT_COLUMN_MAX_WIDTH } from '../components/agent/layout'
import { buildTranscribeEndpoint } from '../hooks/useTranscribeEndpoint'
import { OPENING_MESSAGE, WELCOME_BACK_MESSAGE, coverPrompt, guidedEditReason, guidedNotesReason, isComingBack, skipPrompt } from '../utils/guided'
import GuideIntro from '../components/guided/GuideIntro'
import GuidePanel, { GuideRailDots } from '../components/guided/GuidePanel'
import GuideNoteDialog from '../components/guided/GuideNoteDialog'
import type { GuideNoteView } from '../types/gotypes'

// The server pins the guided chat's model, so the composer offers none.
const NO_MODELS: AgentChatConfig['models'] = []
const TOOL_FORMATTERS = [ptToolFormatter]

// One config per customer, kept, so the chat provider's config keeps its
// identity across renders (its endpoints key the live session's effects).
const CONFIGS = new Map<string, AgentChatConfig>()
function guidedChatConfig(customer: string): AgentChatConfig {
  let config = CONFIGS.get(customer)
  if (!config) {
    config = {
      apiBaseUrl: '',
      models: NO_MODELS,
      endpoints: platinumV2Endpoints(customer),
      getAuthToken,
      plugins: AGENT_PLUGINS,
      toolFormatters: TOOL_FORMATTERS,
      sheet: { maxWidth: AGENT_COLUMN_MAX_WIDTH },
    }
    CONFIGS.set(customer, config)
  }
  return config
}

/** The agenda's print view, in a new tab so the conversation keeps its place. */
export function openGuidedAgenda(customer: string, responseId: string) {
  window.open(`/guided/${encodeURIComponent(customer)}/${encodeURIComponent(responseId)}/agenda`, '_blank', 'noopener')
}

interface NoteDialogState {
  note: GuideNoteView | null
  pointId: string | null
}

function GuidedConversationBody({ customer, responseId }: { customer: string; responseId: string }) {
  const chat = useAgentChat()
  const account = useAccount()
  const who = useWhoToAsk(customer)
  const dictation = useDictationAccess(customer)
  const guided = useGuidedResponse({ customer, responseId, messages: chat.messages, isStreaming: chat.isStreaming })
  const viewerEmail = account.user?.session_info?.email || ''
  const view = guided.view

  // Start or Continue pressed on this visit; an in-progress conversation opens straight in.
  const [entered, setEntered] = useState(false)
  const [panelOpen, setPanelOpen] = useState(true)
  const [noteDialog, setNoteDialog] = useState<NoteDialogState | null>(null)
  const composerHandleRef = useRef<AgentComposerHandle | null>(null)

  const sessionId = view?.response.session_id ?? ''
  const showChat = !!view && !!sessionId && (entered || view.response.status !== 'invited')
  const editReason = view ? guidedEditReason(view, viewerEmail, who) : undefined
  const notesReason = view ? guidedNotesReason(view, viewerEmail, who) : undefined

  // Trigger: the chat to show. Resumes it once per session id.
  const resumedRef = useRef('')
  const chatId = showChat ? sessionId : ''
  useEffect(() => {
    if (!chatId || resumedRef.current === chatId) return
    resumedRef.current = chatId
    void chat.resumeSession(chatId)
  }, [chatId])

  // Trigger: the new chat is loaded. Start sends one opening line for the
  // person (their click), so the agent opens the conversation.
  const openingForRef = useRef('')
  const loadedId = chat.session?.id ?? ''
  useEffect(() => {
    if (!loadedId || openingForRef.current !== loadedId) return
    openingForRef.current = ''
    if (chat.messages.length === 0) void chat.sendMessage(OPENING_MESSAGE)
  }, [loadedId])

  // Trigger: a started conversation opened again after a break. The reply
  // box gets a line for the person to send, so the agent says where they got
  // to and asks the open point again (Matt, 29 Oct 2026). Nothing is sent.
  const welcomedRef = useRef('')
  const hasMessages = chat.messages.length > 0
  useEffect(() => {
    if (!loadedId || !hasMessages || welcomedRef.current === loadedId || editReason) return
    welcomedRef.current = loadedId
    if (!isComingBack(chat.messages, Date.now())) return
    const composer = composerHandleRef.current
    if (composer && !composer.getInput().trim()) composer.setInput(WELCOME_BACK_MESSAGE)
  }, [loadedId, hasMessages])

  const handleStart = async () => {
    if (!view) return
    const fresh = view.response.status === 'invited' && !view.response.session_id
    const id = await guided.handleStart()
    if (!id) return
    if (fresh) openingForRef.current = id
    setEntered(true)
  }

  const fillComposer = (text: string) => {
    composerHandleRef.current?.setInput(text)
  }

  const handleSaveNote = (base: GuideNoteView | null, draft: Parameters<typeof guided.handleAddNote>[0]) =>
    base ? guided.handleSaveNote(base, draft) : guided.handleAddNote(draft)

  if (guided.isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
  }
  if (!view) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', p: 3 }}>
        <Alert severity={guided.notFound ? 'info' : 'error'}>
          {guided.notFound
            ? 'This guided conversation was not found. The link may be for someone else, or the invitation may have been withdrawn.'
            : guided.error || 'This guided conversation could not be loaded.'}
        </Alert>
      </Box>
    )
  }

  if (!showChat) {
    return (
      <GuideIntro
        guide={view.guide}
        points={view.points}
        reviewers={view.reviewers}
        startLabel={view.response.status === 'invited' ? 'Start' : 'Continue'}
        busy={guided.busy === 'start'}
        startDisabledReason={editReason}
        error={guided.actionError}
        onStart={handleStart}
        onOpenAgenda={() => openGuidedAgenda(customer, responseId)}
      />
    )
  }

  const panel = (
    <GuidePanel
      view={view}
      disabledReason={editReason}
      notesDisabledReason={notesReason}
      busy={guided.busy !== ''}
      actionError={guided.actionError}
      onCoverPoint={(p) => fillComposer(coverPrompt(p))}
      onSkipPoint={(p) => fillComposer(skipPrompt(p))}
      onAddNote={(pointId) => setNoteDialog({ note: null, pointId })}
      onEditNote={(note) => setNoteDialog({ note, pointId: note.point_id })}
      onFinish={() => { void guided.handleFinish() }}
      onOpenAgenda={() => openGuidedAgenda(customer, responseId)}
    />
  )

  return (
    <>
      <AgentChat
        apiBaseUrl={API_BASE_URL}
        authHeader={axios.defaults.headers.common['Authorization'] as string | undefined}
        artifactPanelOpen={panelOpen}
        onOpenArtifactPanel={() => setPanelOpen(true)}
        onCloseArtifactPanel={() => setPanelOpen(false)}
        composerDisabled={!!editReason}
        composerPlaceholder={editReason ?? 'Type your answer, or use the microphone to talk…'}
        composerHandleRef={composerHandleRef}
        notice={editReason ? { severity: 'info', text: editReason } : undefined}
        transcribeEndpoint={buildTranscribeEndpoint(customer, chat.session?.job)}
        // An invitee may dictate at any AI level: the server lets an open
        // guided response through /transcribe, billed to their own team.
        dictationDisabledReason={view?.can_edit ? undefined : dictation.reason}
        panelSection={panel}
        panelSectionLabel="Agenda and notes"
        panelSectionRail={<GuideRailDots points={view.points} states={view.states} />}
        // A card pick goes into the composer, for the person to add to and
        // send: Matt wants people to talk, and a click sent too soon (29 Oct).
        askUserAnswersInComposer
      />
      {noteDialog && (
        <GuideNoteDialog
          key={noteDialog.note?.id ?? `new-${noteDialog.pointId ?? ''}`}
          note={noteDialog.note}
          initialPointId={noteDialog.pointId}
          points={view.points}
          busy={guided.busy === 'note'}
          onSave={handleSaveNote}
          onDelete={async (note) => guided.handleDeleteNote(note.id)}
          onClose={() => setNoteDialog(null)}
        />
      )}
    </>
  )
}

export default function GuidedConversationPage() {
  const router = useRouter()
  const account = useAccount()
  const customer = (router.params.customer as string | undefined) || account.customer?.name || ''
  const responseId = (router.params.responseId as string | undefined) || ''
  return (
    <AgentChatProvider config={guidedChatConfig(customer)}>
      <Box data-testid="guided-conversation-page" sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          <GuidedConversationBody customer={customer} responseId={responseId} />
        </Box>
      </Box>
    </AgentChatProvider>
  )
}
